import React, {useState} from "react";

import {Grid} from "@mui/material";
import {FormInput} from "../../../../../components/form/form_inputs/form_input";
import {FormContent} from "../../../../../components/form/form_inputs/form_buttons";
import {Person, PersonDocument, PersonDocumentRestControllerApiFactory, PersonDocumentType,
    PersonDocumentTypeRestControllerApiFactory,} from "arteco-api-client-ts";
import {useForm, UseFormProps,} from "../../../../../components/form/form_model";
import {useApiManager} from "../../../../../components/utils/api";
import {useParams} from "react-router-dom";
import FormFile from "../../../../../components/form/form_inputs/form_file";
import {usePerson} from "../index";
import FormToggle from "../../../../../components/form/form_inputs/form_toggle";
import FormSelect from "../../../../../components/form/form_inputs/form_select";
import {types} from "../../../../../components/form/types";
import {parsePathId} from "../../../../../components/form/form_utils";
import {personName} from "../header";
import {PersonDocumentTypeHint} from "../../persondoctype/hint";
import {autoExpirationFor, hasYearMonth, isAutoExpiration, monthsLabel, yearMonthLabel} from "../../persondoctype/labels";

export const PersonDocumentEditPage = () => {
    const params = useParams();
    const personId = parsePathId(params, "personId");
    const manager = useApiManager();
    const ctrl = manager.factory(PersonDocumentRestControllerApiFactory);
    const apiManager = useApiManager();
    const typeCtrl = manager.factory(PersonDocumentTypeRestControllerApiFactory);

    const [person, setPerson] = usePerson(personId);
    const [docType, setDocType] = useState<PersonDocumentType>();
    // año y mes como estén en la ficha, para enseñar desde cuándo se cuenta la caducidad automática (#14387)
    const [yearMonth, setYearMonth] = useState<{ year?: any, month?: any }>({});

    const form = useForm({
        fetcher: (id) => ctrl.getPersonDocument(id),
        updater: (bean) => ctrl.savePersonDocument(bean),
        remover: (id) => ctrl.deleteOrDismissPersonDocument(id),
        // El documento trae el tipo resumido (id y nombre): la descripción, que es el aviso, se pide aparte.
        postLoad: (bean) => {
            setYearMonth({year: bean.year, month: bean.month});
            if (bean.type?.id) apiManager.execute(typeCtrl.getPersonDocumentType(bean.type.id)).then(setDocType);
        },
        factory: () => {
            return {
                person: {id: personId} as Person,
            } as PersonDocument;
        },
    } as UseFormProps<PersonDocument>);

    const autoHelper = () => {
        const months = monthsLabel(docType?.expirationMonths);
        if (!hasYearMonth(yearMonth.year, yearMonth.month)) {
            return "Automática: " + months + " desde el año y mes, que el tipo pide";
        }
        return "Automática: " + months + " desde " + yearMonthLabel(yearMonth.year, yearMonth.month)
            + " (" + autoExpirationFor(docType, yearMonth.year, yearMonth.month) + ")";
    };

    return (
        <>
            <FormContent
                form={form}
                title={"Documento de " + personName(person)}>

                <FormInput field={"title"} type={"text"} label={"Título"}/>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormSelect field={"type"} type={types.personDocumentTypes} label={"Tipo"}
                                    onChange={item => {
                                        // Sale de un tipo automático (#14381): la fecha la calculó el tipo viejo, no
                                        // la ha puesto nadie. Se vacía; en uno manual hay que indicarla.
                                        if (isAutoExpiration(docType) && !isAutoExpiration(item)) form.triggerField("expiration", null);
                                        setDocType(item);
                                    }}/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        {/* Caducidad automática (#14381): la pone el servidor al guardar, desde el día 1 del año y mes
                            del documento (#14387). Sin ellos, desde la subida; al cambiarlos, se recalcula. */}
                        <FormInput field={"expiration"} type={"date"} label={"Caducidad"}
                                   disabled={isAutoExpiration(docType)}
                                   helperText={isAutoExpiration(docType) ? autoHelper() : undefined}/>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <FormInput field={"year"} type={"number"} label={"Año"}
                                   onChange={v => setYearMonth(ym => ({...ym, year: v}))}/>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <FormInput field={"month"} type={"number"} label={"Mes"}
                                   onChange={v => setYearMonth(ym => ({...ym, month: v}))}/>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormToggle field={"visible"} label={"Visible por el empleado"}/>
                    </Grid>
                </Grid>
                <PersonDocumentTypeHint type={docType}/>

                <FormInput field={"comments"} type={"text"} label={"Comentarios"} rows={5}/>

                <FormFile
                    field={"attachment"}
                    label={"Archivo"}
                    onDownload={() =>
                        apiManager.executeRaw(ctrl.viewPersonDocument(form.getIdOrErr()))
                    }
                />

            </FormContent>
        </>
    );
};